import { Calendar } from "./Calendar";
import { CancellationSearch } from "./CancellationSearch";
import { ConflictSearch } from "./ConflictSearch";
import { EmptyHeader } from "./EmptyHeader";
import { ExamData } from "./ExamData";
import { Header } from "./Header";
import { Limits } from "./Limits";
import { Macros } from "./Macros";
import { MemberData } from "./MemberData";
import { Model } from "./Model";
import { OrderSearch } from "./OrderSearch";
import { PrefsData } from "./PrefsData";
import { RequestSearch } from "./RequestSearch";
import { ReservationSearch } from "./ReservationSearch";
import { Section, TComponentType } from "./Section";
import { Selection } from "./Selection";
import {
    parseDateHeader,
    parseDatePeriodHeader,
    parseObjectHeader,
    parseTimeHeader,
    parseTimePeriodHeader,
    parseWeekdayHeader,
    parseWeekdayPeriodHeader,
    parseWeekHeader,
    parseWeekPeriodHeader,
} from "./utils";
import { WaitingListSearch } from "./WaitingListSearch";
import API from "../lib/TimeEditAPI";
import TemplateKind from "./TemplateKind";
import Language from "../lib/Language";
import _ from "underscore";
import {
    IExportViewsResponse,
    IFindViewsResponse,
    IImportViewsResponse,
} from "../types/api/APIResponseTypes";
import { TViewCreateClass, TViewNameClass } from "../types/api/APIClassTypes";
import { TWithGenericViewData, TTableHeader } from "../types/api/shared";

const DEFAULT_VIEW = "DEFAULT_VIEW";
const MAX_DIMENSION = 100;

type TMacroInView = {
    action: (typeof Macros.Action)[keyof typeof Macros.Action];
    /** Only the macros aimed at a particular calendar carry one. */
    target?: string;
};

/**
 * The flags buildCalendar sets from the view's function list. Any component can
 * carry them, not only Calendar, because buildCalendar assigns them onto
 * whichever component the view data asked for.
 */
type TMacroFlags = {
    updateMacro?: boolean;
    setDayMacro?: boolean;
    scrollDayMacro?: boolean;
    setTimeMacro?: boolean;
    scrollTimeMacro?: boolean;
    nowMacro?: boolean;
};

export type TOrganizationInView = {
    id: number;
    extid: string;
};

type TMutatedViewType = Omit<TViewNameClass, "org_state"> & { orgState: number };

export const ViewOrgVisibility = {
    PRIVATE: 0,
    PUBLIC: 1,
    ORGANIZATIONS: 2,
} as const;

export type ViewName = {
    class: "viewname";
    description: string;
    id: number;
    name: string;
    orgState: number;
};

type TViewOrgVisibilityKey = keyof typeof ViewOrgVisibility;
export type TViewOrgVisibilityValue = (typeof ViewOrgVisibility)[TViewOrgVisibilityKey];

export class View extends Model {
    id: number;
    name: string;
    section: Section | null;
    macros: TMacroInView[];
    organizations: TOrganizationInView[];
    orgState: TViewOrgVisibilityValue;
    isUnsupported: boolean;

    static readonly DEFAULT_VIEW: typeof DEFAULT_VIEW = DEFAULT_VIEW;

    constructor(
        id: number,
        name: string,
        topSection: Section | null,
        macros: TMacroInView[] = [],
        organizations: TOrganizationInView[],
        orgState: TViewOrgVisibilityValue
    ) {
        super("View");
        this.id = id;
        this.name = name;
        this.section = topSection;
        this.macros = macros;
        this.organizations = organizations || [];
        this.orgState = orgState;
        this.isUnsupported = this.hasUnsupportedChild();
    }

    static parse(serverViewData: TViewCreateClass) {
        const viewId: number = serverViewData.id;
        const name: string = serverViewData.name;
        const tables = serverViewData.tables;
        const orgState = serverViewData.org_state as TViewOrgVisibilityValue;
        const organizations: TOrganizationInView[] = serverViewData.orgs
            ? serverViewData.orgs.map(
                  (organization) =>
                      ({
                          id: organization.id,
                          extid: organization.extid,
                      } as TOrganizationInView)
              )
            : [];
        const currentTable = 1;
        const topSection = View.parseSection(tables, currentTable, name);
        let macros: TMacroInView[];
        if (topSection) {
            removeTemporaryProperties(topSection);
            macros = View.parseMacros(topSection);
        }
        return new View(viewId, name, topSection, macros!, organizations, orgState);
    }

    static parseHeaderList(headers: TTableHeader[], limits: Limits) {
        let prevHeader, returnHeader, header;
        for (let i = 0; i < headers.length; i++) {
            header = View.parseHeader(headers[i], limits);
            if (!returnHeader) {
                returnHeader = header;
            }
            if (prevHeader) {
                prevHeader.subheader = header;
            }
            prevHeader = header;
        }

        if (!returnHeader) {
            returnHeader = new EmptyHeader();
        }

        returnHeader.isActive = true;
        return returnHeader;
    }

    static parseCalendar(data: TWithGenericViewData, activeCalendar: number, name: string) {
        const limits = new Limits(data.limits);
        let xHeader, yHeader;

        try {
            xHeader = View.parseHeaderList(data.headersX, limits);
            yHeader = View.parseHeaderList(data.headersY, limits);
        } catch (error) {
            throw new Error(
                `${Language.get("nc_invalid_header_in_calendar_view:", activeCalendar, name)} ${
                    (error as any).message
                }`
            );
        }
        // The extra optional members cover properties that only exist on some of the
        // component classes but are assigned generically below.
        let calendar: (
            | Calendar
            | ReservationSearch
            | CancellationSearch
            | OrderSearch
            | WaitingListSearch
            | RequestSearch
            | PrefsData
            | ExamData
            | MemberData
            | ConflictSearch
        ) & {
            isUnsupported?: boolean;
            isPaddingActive?: boolean;
            typeFilter?: number[];
            colorTypes?: number[];
            periodId?: number;
            isTimePeriod?: boolean;
            macroFunctions?: any[];
            selected?: boolean;
        } & TMacroFlags = new Calendar(xHeader, yHeader, limits);
        if (data.isList) {
            calendar = new ReservationSearch(limits);
        } else if (data.isCancellationList) {
            calendar = new CancellationSearch(limits);
        } else if (data.isOrderList) {
            calendar = new OrderSearch(limits);
        } else if (data.isWaitingList) {
            calendar = new WaitingListSearch(limits);
        } else if (data.isRequestList) {
            calendar = new RequestSearch(limits);
        } else if (data.isDateSelector) {
            calendar.isDateSelector = true;
        } else if (data.isPrefsComponent) {
            calendar = new PrefsData(limits);
        } else if (data.isExamComponent) {
            calendar = new ExamData(limits);
        } else if (data.isGroupManager) {
            calendar = new MemberData(limits);
        } else if (data.isConflictList) {
            calendar = new ConflictSearch(limits);
        }

        // If server has marked the data as incomplete, we consider the resulting calendar to be unsupported
        calendar.isUnsupported = data.isIncomplete || false;

        const booleanProps = [
            "availabilityInForeground",
            "hideAbstractObstacles",
            "hideObstacles",
            "readOnly",
            "hideInfoReservations",
        ];

        booleanProps.forEach((prop) => {
            calendar[prop] = data[prop];
        });
        calendar.isPaddingActive = data.hidePadding !== undefined ? !data.hidePadding : true;
        calendar.privateSelected = data.privateSelectedList;

        calendar.templateKind = TemplateKind.getFromString(data.templateKind);
        if (TemplateKind.equals(TemplateKind.NULL, calendar.templateKind)) {
            calendar.templateKind = TemplateKind.RESERVATION;
        }
        if (!TemplateKind.equals(TemplateKind.RESERVATION, calendar.templateKind)) {
            calendar.privateSelected = true;
        }

        if (calendar.privateSelected === true) {
            calendar.selection = new Selection();
        }

        calendar.typeFilter = data.typeFilter || [];
        calendar.colorTypes = data.typeColor || [];
        calendar.periodId = data.periodId || 0;
        calendar.isTimePeriod = calendar.periodId !== 0;

        const macros = data.function || [];

        calendar.macroFunctions = macros;
        macros.forEach((macro) => {
            if (macro.name === "update") {
                calendar.updateMacro = true;
            } else if (macro.name === "setDay") {
                calendar.setDayMacro = true;
            } else if (macro.name === "scrollDay") {
                calendar.scrollDayMacro = true;
            } else if (macro.name === "setTime") {
                calendar.setTimeMacro = true;
            } else if (macro.name === "scrollTime") {
                calendar.scrollTimeMacro = true;
            } else if (macro.name === "now") {
                calendar.nowMacro = true;
            } else {
                throw new Error(`Unknown macro ${macro.name}`);
            }
        });

        if (data.orientation.selected) {
            calendar.selected = true;
        }

        return calendar;
    }

    static parseHeader(data: TTableHeader, limits) {
        let header: Header | null = null;

        if (data.kind === "date") {
            header = parseDateHeader(data, limits);
        } else if (data.kind === "time") {
            header = parseTimeHeader(data, limits);
        } else if (data.kind === "object") {
            header = parseObjectHeader(data, limits);
        } else if (data.kind === "weekday") {
            header = parseWeekdayHeader(data, limits);
        } else if (data.kind === "week") {
            header = parseWeekHeader(data, limits);
        } else if (data.kind === "periodweek") {
            header = parseWeekPeriodHeader(data, limits);
        } else if (data.kind === "periodweekday") {
            header = parseWeekdayPeriodHeader(data, limits);
        } else if (data.kind === "periodtime") {
            header = parseTimePeriodHeader(data, limits);
        } else if (data.kind === "perioddate") {
            header = parseDatePeriodHeader(data, limits);
        }

        if (!header) {
            // Unimplemented header type
            throw new Error(`No header of type ${data.kind}.`);
        }

        header.limits = limits;
        header.showInfo = data.showInfo;
        header.hide = data.hideHeader;
        header.isScrollable = data.scrollable;
        header.hideGrid = data.hideGrid;
        header.size = data.size;
        header.isSideBySide = data.isSideBySide || false;

        return header;
    }

    static createContainer(
        firstSection: Section & { height?: number; width?: number },
        secondSection: Section & { height?: number; width?: number },
        currentArea: TWithGenericViewData,
        sizeProp: string,
        isVertical: boolean
    ) {
        // console.log({ firstSection: Section, secondSection, currentArea, sizeProp, isVertical });

        const weight =
            firstSection.area?.orientation[sizeProp] /
            (firstSection.area?.orientation[sizeProp] + secondSection.area?.orientation[sizeProp]);

        // eslint-disable-next-line no-param-reassign
        firstSection.height = MAX_DIMENSION;
        // eslint-disable-next-line no-param-reassign
        firstSection.width = MAX_DIMENSION;
        // eslint-disable-next-line no-param-reassign
        secondSection.height = MAX_DIMENSION;
        // eslint-disable-next-line no-param-reassign
        secondSection.width = MAX_DIMENSION;
        // eslint-disable-next-line no-param-reassign
        firstSection[sizeProp] = Math.round(MAX_DIMENSION * weight);
        // eslint-disable-next-line no-param-reassign
        secondSection[sizeProp] = MAX_DIMENSION - firstSection[sizeProp];
        // eslint-disable-next-line no-param-reassign
        currentArea.orientation[sizeProp] =
            firstSection.area?.orientation[sizeProp] + secondSection.area?.orientation[sizeProp];
        const container = new Section(firstSection, secondSection, weight, isVertical);
        container.area = currentArea;
        return container;
    }

    static parseSection(
        areas: TWithGenericViewData[],
        currentCalendar: number,
        name: string
    ): Section | null {
        let section: Section | null;
        if (areas.length === 1) {
            const calendar = View.parseCalendar(areas[0], currentCalendar, name);
            section = new Section(calendar, null, null, null);
            section.area = areas[0];
            return section;
        }

        section = buildSection(areas, false, currentCalendar, name);
        if (!section) {
            section = buildSection(areas, true, currentCalendar, name);
        }

        return section;
    }

    static parseMacros(section: Section): TMacroInView[] {
        const macros: TMacroInView[] = [];
        const calendars = section.getAllChildren();
        calendars.forEach((child, index) => {
            // buildCalendar tacks the macro flags onto any component, so read
            // them off the child rather than narrowing to Calendar first.
            const calendar: TComponentType & TMacroFlags = child;
            if (calendar.scrollDayMacro || calendar.setDayMacro) {
                macros.push({
                    action: Macros.Action.SET_DATE,
                    target: `calendar${index}`,
                });
            }
            if (calendar.scrollTimeMacro || calendar.setTimeMacro) {
                macros.push({
                    action: Macros.Action.SET_TIME,
                    target: `calendar${index}`,
                });
            }
            macros.push({
                action: Macros.Action.REFRESH,
            });
            if (calendar instanceof Calendar) {
                macros.push({
                    action: Macros.Action.SET_RESERVATION,
                });
            }
        });
        return macros;
    }

    static find(
        id: number | null | typeof DEFAULT_VIEW,
        callback: (foundViews: ViewName[] | View | null) => void
    ) {
        if (!id) {
            API.sendMessage("findViews", {}, (response: IFindViewsResponse) => {
                const foundViews = response.parameters[0].map((viewData) => {
                    const mutatedView: Partial<TViewNameClass> & Partial<TMutatedViewType> =
                        viewData;
                    // eslint-disable-next-line no-param-reassign
                    mutatedView.orgState = viewData.org_state;
                    // eslint-disable-next-line no-param-reassign
                    delete mutatedView.org_state;
                    return mutatedView as ViewName;
                });
                callback(foundViews);
            });
        } else {
            API.sendMessage(
                "exportViews",
                { viewIds: id === DEFAULT_VIEW ? [] : [id] },
                (response: IExportViewsResponse) => {
                    if (
                        !Array.isArray(response.parameters[0]) ||
                        response.parameters[0].length === 0
                    ) {
                        return callback(null);
                    }

                    try {
                        const parsedView = View.parse(response.parameters[0][0]);
                        return callback(parsedView);
                    } catch (e) {
                        return callback(e as any);
                    }
                }
            );
        }
    }

    static save(
        id: number,
        name: string,
        tables: TWithGenericViewData[],
        organizations: { extid: string; id: number }[] = [],
        orgState: TViewOrgVisibilityValue | null,
        callback: (results: IImportViewsResponse["parameters"][0]) => void
    ) {
        const serverOrganizations = organizations.map((organization) => ({
            class: "organizationid",
            extid: organization.extid,
            id: organization.id,
        }));
        let viewData;
        // Only send orgState if we're hitting the "make public" button, otherwise send nothing
        if (orgState !== null) {
            viewData = [
                [
                    {
                        class: "viewcreate",
                        id,
                        name,
                        orgs: serverOrganizations,
                        tables,
                        org_state: orgState,
                    },
                ],
            ];
        } else {
            viewData = [[{ class: "viewcreate", id, name, orgs: serverOrganizations, tables }]];
        }
        API.sendMessage("importViews", viewData, (response: IImportViewsResponse) => {
            callback(response.parameters[0]);
        });
    }

    static delete(id: number, callback: () => void) {
        API.sendMessage("deleteViews", [[{ class: "viewid", id }]], callback);
    }

    hasUnsupportedChild() {
        const children = (this.section as any)?.getAllChildren?.();
        return _.some(children, (child) => child.isUnsupported);
    }
}

const buildSection = function (
    areas: TWithGenericViewData[],
    isVertical: boolean,
    currentCalendar: number,
    name: string
) {
    let distanceProp = "y",
        sizeProp = "height";
    if (!isVertical) {
        distanceProp = "x";
        sizeProp = "width";
    }

    let first: TWithGenericViewData[] = [];
    let second: TWithGenericViewData[] = [];
    let divider;

    let currentArea: TWithGenericViewData,
        i: number,
        j: number,
        area: TWithGenericViewData,
        distance: number,
        firstSection: Section | null,
        secondSection: Section | null;
    const numAreas = areas.length;
    const PRECISION = 3;

    for (i = 0; i < numAreas; i++) {
        currentArea = areas[i];

        first = [];
        second = [];
        divider = parseFloat(
            (currentArea.orientation[distanceProp] + currentArea.orientation[sizeProp]).toFixed(
                PRECISION
            )
        );

        for (j = 0; j < numAreas; j++) {
            area = areas[j];
            distance = parseFloat(area.orientation[distanceProp].toFixed(PRECISION));
            if (parseFloat((distance + area.orientation[sizeProp]).toFixed(PRECISION)) <= divider) {
                first.push(area);
            } else if (distance >= divider) {
                second.push(area);
            } else {
                break;
            }
        }

        if (
            first.length !== 0 &&
            second.length !== 0 &&
            first.length + second.length === areas.length
        ) {
            firstSection = View.parseSection(first, currentCalendar, name);
            secondSection = View.parseSection(second, currentCalendar, name);
            if (firstSection !== null && secondSection !== null) {
                return View.createContainer(
                    firstSection,
                    secondSection,
                    currentArea,
                    sizeProp,
                    isVertical
                );
            }
        }
    }
    return null;
};

const removeTemporaryProperties = function (section: Section) {
    if (!section) {
        return;
    }

    if (section.area) {
        // eslint-disable-next-line no-param-reassign
        delete section.area;
    }
    removeTemporaryProperties(section.first as any);
    removeTemporaryProperties(section.second as any);
};
